<template>
  <div class="time">
    <label class="time__label">{{ label }}</label>
    <div class="time__input">
      <ActionButton
        label=""
        class="mr-3 mt-0"
        :class="
          `${btnClass} ${
            disabled && (label === 'End Time' || label === 'Ends')
              ? 'disabled'
              : ''
          }`
        "
        mode="outline"
        :theme="{ primary, secondary, event, danger }"
        @customEvent="showDropdown = !showDropdown"
        v-on-clickaway="handleDropDownClose"
        >{{ disabled ? "00:00" : buttonPlaceholder }}</ActionButton
      >
      <div class="time__input-wrapper">
        <ul
          class="time__input-wrapper__list block block-shadow"
          :class="{
            show: showDropdown
          }"
        >
          <li
            ref="time__inputs"
            v-for="item in items"
            :key="`time__input-${item.value}`"
            class="time__input-wrapper__list-item"
            :class="{
              disabled: item.disabled
            }"
            tabindex="0"
            @click="handleItemClick(item)"
          >
            {{ item.name }}
          </li>
        </ul>
      </div>
    </div>
    <button
      @click.prevent="() => handleClearTime()"
      :class="`clear-button ${disabled ? 'clear-disabled' : ''}`"
      type="button"
      v-if="isClearable"
    >
      Clear
    </button>
  </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";

const ActionButton = () => import("@/components/ActionButton");

export default {
  mixins: [clickaway],
  components: {
    ActionButton
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    timeOnly: {
      type: Boolean,
      default: false
    },
    handleClearTime: {
      type: Function,
      default: () => {}
    },
    isClearable: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: "Time"
    },
    btnClass: [String],
    placeholder: {
      type: String,
      default: "Choose time"
    },
    value: {
      type: [String],
      default: null
    },
    disabledItems: {
      type: [Array],
      default: () => []
    },
    items: {
      type: [Array],
      default: () => []
    }
  },
  data() {
    return {
      buttonPlaceholder: this.placeholder,
      showDropdown: false
    };
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    }
  },
  watch: {
    value() {
      this.setButtonPlaceholder();
    },
    items() {
      this.setButtonPlaceholder();
    }
  },
  mounted() {
    this.$nextTick(() => this.setButtonPlaceholder());
  },
  methods: {
    handleItemClick(item) {
      if (this.disabled) this.handleClearTime();
      this.$emit("input", item.value);
      this.$emit("change");
      this.showDropdown = !this.showDropdown;
    },
    setButtonPlaceholder() {
      if (this.items.length === 0) return;

      let item = this.items.find(item => item.value === this.value);
      let itemIndex = getIndex(this.items, item => item.disabled === false);

      if (item) {
        itemIndex = getIndex(this.items, i => i.value === item.value);
      }

      if (item && item.disabled === true) {
        item = this.items.find(item => item.disabled === false);
        itemIndex = getIndex(this.items, i => i.disabled === false);
      }
      this.buttonPlaceholder = this.items[itemIndex].name;
      const itemNode = this.$refs.time__inputs[Math.max(itemIndex - 1, 0)];
      if (!this.timeOnly) itemNode.scrollIntoView({ block: "start" });
    },
    handleDropDownClose() {
      this.showDropdown = false;
    }
  }
};

function getIndex(items, predicate) {
  const index = items.findIndex(predicate);
  if (index < 0) return 0;
  return index;
}
</script>

<style lang="scss" scoped>
.time {
  width: 100%;
  &__label {
    font-size: 12px;
    font-weight: 700;
    color: #4c4c4c;
  }
  &__input {
    &-trigger {
      padding: 8px 14px;
      font-size: $font-size-xs;
      width: 100%;
      @media (max-width: 767px) {
        padding: 8px 14px;
      }
    }
    &-wrapper {
      position: relative;
      border-radius: 10px;
      margin-top: 0.5rem;
      &__list {
        background-color: $white;
        list-style-type: none;
        max-height: 210px;
        overflow: auto;
        padding: 0;
        position: absolute;
        top: 0;
        min-width: 100%;
        visibility: hidden;
        z-index: 1;
        border-radius: 5px;
        &-item {
          cursor: pointer;
          font-size: 15px;
          margin: 0;
          padding: 10px 15px;
          transition: background-color 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
          &:focus,
          &:hover {
            background-color: $gray-200;
          }
          &.disabled {
            background-color: #dcdcdc;
            color: #949494;
            pointer-events: none;
          }
        }
        &.show {
          opacity: 1;
          visibility: visible;
        }
      }
    }
  }
  .clear-button {
    all: unset;
    color: $brand-primary-v2;
    font-size: 0.875rem;
    font-weight: 600;
    margin-left: 1rem;
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }

    &.clear-disabled {
      color: #b7b7b7;
      pointer-events: none;
    }
  }

  .disabled {
    pointer-events: none;
    color: #ccc !important;
    background-color: #f8f8f8 !important;
  }
}
</style>
